import Vue from "vue";

//set Headers payload
const authHeader = process.env.VUE_APP_REQUEST_HEADER;
const headers = {};
headers[authHeader] = process.env.VUE_APP_HUB_API_KEY;

function errorMessage(error, fallback) {
  if (error && error.body && (error.body.error || error.body.message)) {
    return error.body.error || error.body.message;
  }
  return fallback;
}

/**
 * The fence pricing structure (base prices, model adjustments, discounts, thresholds, VAT and
 * region service fees) is the same whether it is stored in the configurator db or in HUB's own.
 * Only the API prefix differs, so both stores are built from this factory and the components
 * are pointed at one or the other through their `namespace` prop.
 *
 * The model list and its slat geometry are the exception: only HUB stores them, so they are
 * opt-in through `options.models`. A store without them keeps `models` empty and never calls the
 * endpoint, which is what the configurator store wants - it has no such route.
 *
 * @param apiPrefix e.g. "api/configurator-app/" or "api/fence-pricing/"
 * @param options   { models: true } to enable the model list actions
 */
export default function createFencePricingModule(apiPrefix, options) {
  const base = process.env.VUE_APP_HUB_API_HOST + apiPrefix;
  const hasModels = !!(options && options.models);

  const pricingConfigEndpoint = base + "pricing-config";
  const basePricesEndpoint = base + "base-prices";
  const modelsEndpoint = base + "models";
  const modelAdjustmentsEndpoint = base + "model-adjustments";
  const regionFeesEndpoint = base + "region-service-fees";

  return {
    namespaced: true,
    state: () => ({
      loadingPricingConfig: false,
      savingPricingConfig: false,
      pricingConfigError: null,
      pricingConfig: null,
      loadingBasePrices: false,
      savingBasePrices: false,
      basePricesError: null,
      basePrices: null,
      loadingModels: false,
      savingModels: false,
      modelsError: null,
      // [{ code, name, description, slatWidths, minGap, sortOrder }], HUB-only, empty elsewhere
      models: [],
      loadingModelAdjustments: false,
      savingModelAdjustments: false,
      modelAdjustmentsError: null,
      modelAdjustments: null,
      loadingRegionFees: false,
      savingRegionFees: false,
      regionFeesError: null,
      // keyed by country, loaded on demand: { RO: [{ region, measure, montage, shipping }], … }
      regionFees: {},
    }),
    mutations: {
      setLoadingPricingConfig(state, item) {
        state.loadingPricingConfig = item
      },
      setSavingPricingConfig(state, item) {
        state.savingPricingConfig = item
      },
      setPricingConfigError(state, item) {
        state.pricingConfigError = item
      },
      loadPricingConfig(state, config) {
        state.pricingConfig = config
      },
      // each field is a full replace of its column, same as the configurator API
      updatePricingConfig(state, payload) {
        state.pricingConfig = Object.assign({}, state.pricingConfig, payload)
      },
      setLoadingBasePrices(state, item) {
        state.loadingBasePrices = item
      },
      setSavingBasePrices(state, item) {
        state.savingBasePrices = item
      },
      setBasePricesError(state, item) {
        state.basePricesError = item
      },
      loadBasePrices(state, prices) {
        state.basePrices = prices
      },
      // base prices are a real partial update upstream, so only the changed columns are merged in
      updateBasePrices(state, { country, prices }) {
        state.basePrices = Object.assign({}, state.basePrices, {
          [country]: Object.assign({}, state.basePrices[country], prices)
        })
      },
      setLoadingModels(state, item) {
        state.loadingModels = item
      },
      setSavingModels(state, item) {
        state.savingModels = item
      },
      setModelsError(state, item) {
        state.modelsError = item
      },
      loadModels(state, models) {
        state.models = models
      },
      // partial upsert upstream: only the models sent change, and only their sent keys
      updateModelGeometry(state, geometry) {
        state.models = state.models.map(
          model => geometry[model.code] ? Object.assign({}, model, geometry[model.code]) : model
        )
      },
      setLoadingModelAdjustments(state, item) {
        state.loadingModelAdjustments = item
      },
      setSavingModelAdjustments(state, item) {
        state.savingModelAdjustments = item
      },
      setModelAdjustmentsError(state, item) {
        state.modelAdjustmentsError = item
      },
      // an empty map comes back from PHP as [], so it is normalised to a plain object here
      loadModelAdjustments(state, adjustments) {
        state.modelAdjustments = Object.assign({}, adjustments)
      },
      // partial upsert upstream: models left out keep their value, 0 clears a markup
      updateModelAdjustments(state, adjustments) {
        state.modelAdjustments = Object.assign({}, state.modelAdjustments, adjustments)
      },
      setLoadingRegionFees(state, item) {
        state.loadingRegionFees = item
      },
      setSavingRegionFees(state, item) {
        state.savingRegionFees = item
      },
      setRegionFeesError(state, item) {
        state.regionFeesError = item
      },
      loadRegionFees(state, { country, fees }) {
        state.regionFees = Object.assign({}, state.regionFees, { [country]: fees })
      },
      // only the saved regions are merged back in, the rest of the country keeps its rows
      updateRegionFees(state, { country, fees }) {
        const changed = {};
        fees.forEach(row => { changed[row.region] = row });

        state.regionFees = Object.assign({}, state.regionFees, {
          [country]: (state.regionFees[country] || []).map(
            row => changed[row.region] ? Object.assign({}, row, changed[row.region]) : row
          )
        })
      },
    },
    actions: {
      async fetchPricingConfig({ commit }) {
        commit("setLoadingPricingConfig", true);
        commit("setPricingConfigError", null);

        try {
          const response = await Vue.http.get(pricingConfigEndpoint, {
            headers: headers
          });
          commit("loadPricingConfig", response.body);
        } catch (error) {
          console.error("Error fetching pricing config:", error);
          commit("setPricingConfigError", errorMessage(error, "Nu s-a putut incarca configurarea de preturi."));
        } finally {
          commit("setLoadingPricingConfig", false);
        }
      },
      // payload is a partial config: { gapRates }, { volumeTiers }, { minArea }, { vatRate }
      async savePricingConfig({ commit }, payload) {
        commit("setSavingPricingConfig", true);

        try {
          const response = await Vue.http.patch(pricingConfigEndpoint, payload, {
            headers: headers
          });
          commit("updatePricingConfig", (response.body && response.body.updated) || payload);
          M.toast({ html: "Configurare salvata", classes: "bg-green-500" });
          return true;
        } catch (error) {
          console.error("Error saving pricing config:", error);
          M.toast({
            html: errorMessage(error, "Configurarea nu a putut fi salvata."),
            classes: "bg-red-500"
          });
          return false;
        } finally {
          commit("setSavingPricingConfig", false);
        }
      },
      async fetchBasePrices({ commit }) {
        commit("setLoadingBasePrices", true);
        commit("setBasePricesError", null);

        try {
          const response = await Vue.http.get(basePricesEndpoint, {
            headers: headers
          });
          commit("loadBasePrices", response.body);
        } catch (error) {
          console.error("Error fetching base prices:", error);
          commit("setBasePricesError", errorMessage(error, "Nu s-au putut incarca preturile de baza."));
        } finally {
          commit("setLoadingBasePrices", false);
        }
      },
      // changes is keyed by country: { RO: { price_panel: 185 }, AT: { … } }
      // the endpoint takes one country per call, so a save can be several requests
      async saveBasePrices({ commit }, changes) {
        commit("setSavingBasePrices", true);
        const failed = [];

        for (const country of Object.keys(changes)) {
          try {
            await Vue.http.patch(basePricesEndpoint, {
              country: country,
              prices: changes[country]
            }, {
              headers: headers
            });
            commit("updateBasePrices", { country: country, prices: changes[country] });
          } catch (error) {
            console.error("Error saving base prices for " + country + ":", error);
            failed.push(country + " (" + errorMessage(error, "eroare") + ")");
          }
        }

        commit("setSavingBasePrices", false);

        if (failed.length > 0) {
          M.toast({ html: "Nu s-au salvat: " + failed.join(", "), classes: "bg-red-500" });
          return false;
        }

        M.toast({ html: "Preturi de baza salvate", classes: "bg-green-500" });
        return true;
      },
      // no-ops on a store built without `models`, so a shared component can dispatch it blindly
      async fetchModels({ commit }) {
        if (!hasModels) return;

        commit("setLoadingModels", true);
        commit("setModelsError", null);

        try {
          const response = await Vue.http.get(modelsEndpoint, {
            headers: headers
          });
          commit("loadModels", response.body);
        } catch (error) {
          console.error("Error fetching fence models:", error);
          commit("setModelsError", errorMessage(error, "Nu s-au putut incarca modelele."));
        } finally {
          commit("setLoadingModels", false);
        }
      },
      // geometry is keyed by model code: { 'H150/20': { slatWidths: [150, 20], minGap: 20 } }
      async saveModelGeometry({ commit }, geometry) {
        if (!hasModels) return false;

        commit("setSavingModels", true);

        try {
          await Vue.http.patch(modelsEndpoint, { models: geometry }, {
            headers: headers
          });
          commit("updateModelGeometry", geometry);
          M.toast({ html: "Geometrie salvata", classes: "bg-green-500" });
          return true;
        } catch (error) {
          console.error("Error saving fence model geometry:", error);
          M.toast({
            html: errorMessage(error, "Geometria nu a putut fi salvata."),
            classes: "bg-red-500"
          });
          return false;
        } finally {
          commit("setSavingModels", false);
        }
      },
      async fetchModelAdjustments({ commit }) {
        commit("setLoadingModelAdjustments", true);
        commit("setModelAdjustmentsError", null);

        try {
          const response = await Vue.http.get(modelAdjustmentsEndpoint, {
            headers: headers
          });
          commit("loadModelAdjustments", response.body);
        } catch (error) {
          console.error("Error fetching model adjustments:", error);
          commit("setModelAdjustmentsError", errorMessage(error, "Nu s-au putut incarca ajustarile pe modele."));
        } finally {
          commit("setLoadingModelAdjustments", false);
        }
      },
      // adjustments is a map of model => fraction: { V20: 0.2 }. Only the changed models are sent.
      async saveModelAdjustments({ commit }, adjustments) {
        commit("setSavingModelAdjustments", true);

        try {
          await Vue.http.patch(modelAdjustmentsEndpoint, { adjustments: adjustments }, {
            headers: headers
          });
          commit("updateModelAdjustments", adjustments);
          M.toast({ html: "Ajustari salvate", classes: "bg-green-500" });
          return true;
        } catch (error) {
          console.error("Error saving model adjustments:", error);
          M.toast({
            html: errorMessage(error, "Ajustarile nu au putut fi salvate."),
            classes: "bg-red-500"
          });
          return false;
        } finally {
          commit("setSavingModelAdjustments", false);
        }
      },
      // fees are per region and loaded one country at a time
      async fetchRegionServiceFees({ commit }, country) {
        commit("setLoadingRegionFees", true);
        commit("setRegionFeesError", null);

        try {
          const response = await Vue.http.get(regionFeesEndpoint + "/" + encodeURIComponent(country), {
            headers: headers
          });
          commit("loadRegionFees", { country: country, fees: response.body });
        } catch (error) {
          console.error("Error fetching region service fees for " + country + ":", error);
          commit("setRegionFeesError", errorMessage(error, "Nu s-au putut incarca taxele pe regiuni."));
        } finally {
          commit("setLoadingRegionFees", false);
        }
      },
      // fees is an array of the changed rows only: [{ region, measure, montage, shipping }]
      // partial upsert upstream, so the regions left out keep their values
      async saveRegionServiceFees({ commit }, { country, fees }) {
        commit("setSavingRegionFees", true);

        try {
          await Vue.http.patch(regionFeesEndpoint, { country: country, fees: fees }, {
            headers: headers
          });
          commit("updateRegionFees", { country: country, fees: fees });
          M.toast({ html: "Taxe salvate", classes: "bg-green-500" });
          return true;
        } catch (error) {
          console.error("Error saving region service fees for " + country + ":", error);
          M.toast({
            html: errorMessage(error, "Taxele nu au putut fi salvate."),
            classes: "bg-red-500"
          });
          return false;
        } finally {
          commit("setSavingRegionFees", false);
        }
      },
    }
  }
}
